<template>
  <div :class="{ 'disabled-state': disabled }">
    <div :class="[
      'w-tabs__bar',
      componentClass,
      { 'w-tabs__bar--secondary': secondary },
    ]">
      <slot name="prepend-tabs">
        <w-back-button
          v-if="showArrows || isOverflowing"
          class="mr-2"
          :disabled="prevEntryDisabled"
          @click="invokePrevTabAction"
        />
      </slot>

      <v-tabs
        ref="tabsRef"
        :key="tabsKey"
        v-model="currentTab"
        class="w-tabs__bar__tabs"
        :grow="grow"
        :center-active="centerActive"
        :fixed-tabs="fixedTabs"
        :optional="optional"
        mobile-breakpoint="xs"
        active-class="active_tab"
        height="4rem"
      >
        <v-tabs-slider
          :class="`w-tabs__slider--${getTabState(internalActiveTab)}`"
        />
        <v-tab
          v-for="(tab, index) in internalTabs"
          :key="index"
          :class="`w-tabs__tab--${getTabState(tab)}`"
          :disabled="tab.disabled || disabled"
        >
          <slot
            name="tab-text"
            :tab="tab"
          >
            {{ tab.text || tab }}
            <div
              v-if="tab.tabTitleAppend"
              class="ml-1"
            >
              {{ tab.tabTitleAppend }}
            </div>
          </slot>
          <div
            v-if="$scopedSlots.icon"
            class="tab-icon-wrapper"
          >
            <slot
              name="icon"
              :item="{ tab }"
            />
          </div>
          <slot
            name="item"
            :item="{ tab }"
          />
        </v-tab>
      </v-tabs>

      <slot name="append-tabs">
        <w-next-button
          v-if="showArrows || isOverflowing"
          class="ml-2"
          :disabled="nextEntryDisabled"
          @click="invokeNextTabAction"
        />
      </slot>

      <div
        v-if="$slots['append-end']"
        class="w-tabs__bar__append"
        :class="{ 'w-tabs__bar__append--separated': separateAppend }"
      >
        <slot name="append-end" />
      </div>
    </div>
    <div
      v-if="contentless === false"
      :class="['w-tabs__content', contentClass]"
    >
      <slot>
        <keep-alive>
          <component
            :is="internalActiveComponent"
            v-bind="internalActiveProps"
          />
        </keep-alive>
      </slot>
    </div>
  </div>
</template>

<script>
import tabsMixin from '@/mixins/tabs-mixin';
import debounce from 'lodash/debounce';

export default {
  name: 'WorkBaseTabs',
  mixins: [
    tabsMixin,
  ],
  props: {
    // Data
    tabs: { type: Array, default: () => [] },
    componentClass: { type: String, required: false },
    contentClass: { type: String, required: false },
    value: { type: Number, default: 0 },
    // Functions
    getTabs: { type: Function, required: false },
    activeComponent: { type: Function, required: false },
    // Flags
    separateAppend: Boolean,
    secondary: Boolean,
    table: Boolean,
    grow: { type: Boolean, default: false },
    centerActive: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    fixedTabs: { type: Boolean, default: false },
    contentless: { type: Boolean, default: false },
    optional: { type: Boolean, default: false },
    // Actions
    showArrows: { type: Boolean, default: false },
    nextEntryButtonIsDisabled: { type: Boolean, default: null },
    prevEntryButtonIsDisabled: { type: Boolean, default: null },
    handleNextTabSpecific: { type: Function, required: false },
    handlePrevTabSpecific: { type: Function, required: false },
    beforeHandleNextTapPromise: {
      type: Function,
      default: () => Promise.resolve(),
    },
    beforeHandlePrevTapPromise: {
      type: Function,
      default: () => Promise.resolve(),
    },
    stepCounter: { type: Number, default: 1 },
  },
  data() {
    return {
      currentTab: this.value,
      isOverflowing: false,
      tabsKey: 0,
    };
  },
  computed: {
    internalTabs() {
      if (this.getTabs != undefined) {
        return this.getTabs(this.tabs);
      }
      return this.getOnlyShowableTabs(this.tabs);
    },
    internalActiveTab() {
      return this.getActiveTab(this.currentTab, this.internalTabs);
    },
    internalActiveComponent() {
      if (this.activeComponent != undefined) {
        return this.activeComponent(this.currentTab, this.internalTabs);
      }
      return this.getActiveComponent(this.currentTab, this.internalTabs);
    },
    internalActiveProps() {
      if (this.activeComponent != undefined) {
        return this.activeComponent(this.currentTab, this.internalTabs)?.props;
      }
      return this.getActiveProps(this.currentTab, this.internalTabs);
    },
    nextEntryDisabled() {
      if (this.nextEntryButtonIsDisabled != undefined) {
        return this.nextEntryButtonIsDisabled;
      }
      return this.value >= this.internalTabs.length - 1;
    },
    prevEntryDisabled() {
      if (this.prevEntryButtonIsDisabled != undefined) {
        return this.prevEntryButtonIsDisabled;
      }
      return this.value <= 0;
    },
  },
  watch: {
    value(index) {
      this.currentTab = index;
    },
    currentTab: {
      handler() {
        this.emitTabIndex();
        this.emitCurrentTabObject();
      },
      immediate: true,
    },
    tabs() {
      this.emitTabIndex();
      this.emitCurrentTabObject();
    },
    internalActiveComponent: {
      handler(activeComponent) {
        this.$emit('currentComponent', activeComponent);
      },
      immediate: true,
    },
    internalActiveTab: {
      handler(activeTab) {
        this.$emit('currentTab', activeTab);
      },
      immediate: true,
    },
  },
  mounted() {
    this.$watch(() => this.getIsTabBarOverflowing(), this.setIsOverlowing);

    window.addEventListener('resize', this.onResize, {
      passive: true,
    });
  },
  beforeDestroy() {
    this.cleanup();
  },
  deactivated() {
    this.cleanup();
  },
  methods: {
    emitTabIndex() {
      this.$emit('input', this.currentTab);
    },
    emitCurrentTabObject() {
      this.$emit('currentTabObj', this.internalTabs[this.currentTab]);
    },
    getTabState(tab) {
      if (tab?.state == undefined) {
        if (!tab) return;
        return tab?.value == this.internalActiveTab?.value
          ? 'undefined--active'
          : 'undefined--inactive';
      }
      return tab.state;
    },
    invokeNextTabAction() {
      if (this.handleNextTabSpecific != undefined) {
        return this.handleNextTabSpecific();
      }
      return this.handleNextTabDefault();
    },
    handleNextTabDefault() {
      if (this.disabled) return;
      this.beforeHandleNextTapPromise()
        .then(() => {
          this.$emit('input', this.value + this.stepCounter);
        })
        .catch(() => { });
    },
    invokePrevTabAction() {
      if (this.handlePrevTabSpecific != undefined) {
        return this.handlePrevTabSpecific();
      }
      return this.handlePrevTabDefault();
    },
    handlePrevTabDefault() {
      if (this.disabled) return;
      this.beforeHandlePrevTapPromise()
        .then(() => {
          this.$emit('input', this.value - this.stepCounter);
        })
        .catch(() => { });
    },
    getIsTabBarOverflowing() {
      return this.$refs.tabsRef?.$children?.[0]?.isOverflowing;
    },
    setIsOverlowing(isOverflowing) {
      this.isOverflowing = isOverflowing;
    },
    onResize() {
      debounce(() => {
        this.setIsOverlowing(this.getIsTabBarOverflowing());
        // updates slider
        this.tabsKey += 1;
      }, 250);
    },
    changeTabTo(tabId) {
      let tab = this.internalTabs.find((tab) => tab.id === tabId);
      if (tab == undefined) {
        return;
      }
      this.$emit('input', tab.value);
    },
    cleanup() {
      window.removeEventListener('resize', this.onResize, {
        passive: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* ### DEFAULT STYLING ### */
::v-deep .w-tabs__bar .v-item-group {

  .v-slide-group__prev,
  .v-slide-group__next {
    display: none !important;
  }
}

.w-tabs {
  &__bar {
    display: flex;
    align-items: center;
    @include with-h-line;

    &__tabs {
      min-width: 0;
    }

    &__append {
      flex: none;
      padding-left: 1rem;
      display: flex;
      align-items: center;

      &--separated {
        box-shadow: inset 0 (-$slider-line__thickness--inactive) white;
      }
    }
  }

  &__content {
    padding-top: $content__y-offset;
  }
}

.v-tab :before {
  background-color: $base-hover-color;
}

.theme--light.v-tabs> ::v-deep.v-tabs-bar {
  background-color: $tabs-bg-color;
}

.theme--light .v-tabs {
  .v-tab:hover:before {
    opacity: $base-hover-opacity;
  }
}

::v-deep .v-item-group {
  @include with-h-line;
}

::v-deep .v-tabs-slider-wrapper {
  height: $slider-line__thickness--active !important;
}

::v-deep .w-tabs__bar--secondary>.w-tabs__bar__tabs>.v-tabs-bar {
  height: 2.75rem;
}

::v-deep .w-tabs__content--table .w-data-table.w-data-table--hide-header tbody>tr:first-child>td {
  border-top: none !important;
}

.disabled-state {
  opacity: $disabled-opacity;
}

/* ### ICON ### */
.tab-icon-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: 0.5rem;

  ::v-deep>* {
    height: 0.625rem !important;
    width: 0.625rem !important;
  }
}

/* ### COLORS ### */
.w-tabs__bar ::v-deep .w-tabs__tab,
.w-tabs__slider {
  &--error {
    color: var(--v-error-base) !important;
  }

  &--warning {
    color: var(--v-warning-base) !important;
  }

  &--success {
    color: var(--v-success-base) !important;
  }

  &--default {
    color: var(--v-accent-base) !important;
  }

  &--undefined {
    color: $tab__text-color--inactive !important;

    &--active {
      color: $tab__text-color--active !important;
    }

    &--inactive {
      color: $tab__text-color--inactive !important;
    }
  }
}

/* ### MOBILE */
::v-deep .w-tabs__bar .v-tabs-bar--is-mobile {
  .v-tab:not(.active_tab) {
    display: none;
  }

  .v-tab.active_tab {
    width: 100%;
  }

  .v-tabs-slider-wrapper {
    width: 100% !important;
  }
}
</style>
